import React, { useState, useContext } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive/media';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { fg } from '@confluence/feature-gating';
import { LoadableLazy, useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import {
	BREADCRUMBS_EXPERIENCE,
	BREADCRUMBS_DROPDOWN_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { useEmbeddedModernHeader } from '@confluence/embedded-confluence-allowed-features/entry-points/modern-header.ts';
import { expVal } from '@confluence/feature-experiments';

import { preloadBreadcrumbsDropdown } from './preloadBreadcrumbsDropdown.tsx';
import { buildContentPath } from './breadcrumbsHelpers.ts';

const BreadcrumbsDropdownContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-BreadcrumbsDropdownContent" */ './BreadcrumbsDropdownContent.tsx'
			)
		).BreadcrumbsDropdownContent,
});

const BreadcrumbsPlaceholder = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-BreadcrumbsPlaceholder" */ './BreadcrumbsPlaceholder.tsx'
			)
		).BreadcrumbsPlaceholder,
});

const separatorStyles = css({
	color: token('color.text.subtle'),
	pointerEvents: 'none',
	flexShrink: '0',
	marginRight: token('space.050'),
	marginLeft: token('space.050'),
});

const hiddenStyles = css({
	display: 'none',
});

const hiddenContentTitleStyles = css({
	opacity: 0,
	maxWidth: '0px',
});

const hideSeparatorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.xs]: {
		display: 'none',
	},
});

const breadcrumbsStyles = xcss({
	minWidth: '32px', // needed for responsive breadcrumbs, 32px is the width of the chevron button
	alignItems: 'center',
	flex: '0 1 auto',
	gap: 'space.100',
});

const inlineRenameBreadcrumbsStyles = xcss({
	flex: '0 0 auto',
});

const fadedSpaceNameTextStyles = css({
	maskImage: 'linear-gradient(to right, black 60%, transparent 100%)',
	WebkitMaskImage: 'linear-gradient(to right, black 60%, transparent 100%)',
});

const spaceNameTextStyles = css({
	textOverflow: 'ellipsis',
	width: '100%',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	maxWidth: '100%',
	font: token('font.body'),
});

const noEllipsisStyles = css({
	textOverflow: 'unset',
});

const breadcrumbDropdownTriggerStyles = css({
	display: 'inline-block',
	cursor: 'pointer',
	flexShrink: 0,
	width: '100%',
	borderRadius: token('radius.small'),
	border: 'transparent',
	backgroundColor: token('color.background.neutral.subtle'),
	color: token('color.text.subtlest'),
	padding: '0',
	paddingBlock: '0',
	paddingInline: '0',
	'&:hover': {
		textDecoration: 'underline',
		color: token('color.text.subtle'),
	},
	'&:disabled': {
		cursor: 'default',
		'&:hover': {
			textDecoration: 'none',
			color: token('color.text.subtlest'),
		},
	},
	'&:focus': {
		color: token('color.text.subtle'),
	},
	'&:active': {
		color: token('color.text'),
	},
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
	},
});

const maxWidthBreadcrumbsDropdownTriggerStyles = css({
	maxWidth: '240px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.md]: {
		maxWidth: '92px',
	},
	width: 'unset',
});

const onlySpaceNameTriggerStyles = css({
	flex: '0 1 auto',
	overflow: 'hidden',
	maxWidth: '400px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.md]: {
		maxWidth: '400px',
	},
	width: 'unset',
});

const maxWidthOnlyContentTitleTriggerStyles = css({
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.md]: {
		maxWidth: '100%',
	},
	textOverflow: 'ellipsis',
});

const currentPageBreadcrumbStyles = css({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.xsmall'),
	minWidth: '24px',
	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.xs]: {
		display: 'none',
	},
});

const currentPageLinkBreadcrumbStyles = css({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.xsmall'),
	minWidth: '24px',
	color: token('color.text'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[media.below.xs]: {
		display: 'none',
	},

	'&:hover': {
		color: token('color.link'),
		textDecoration: 'underline',
	},

	'&:visited': {
		color: token('color.text'),
	},

	'&:visited:active': {
		color: token('color.text'),
	},
});

const contentTitleStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	opacity: 1,
	overflow: 'hidden',
	maxWidth: '800px',
	transition: 'none',
});

const dropdownLessContentTitleStyles = css({
	paddingInlineStart: token('space.100'),
});

const chevronBaseStyles = xcss({
	width: '32px',
	minWidth: '32px',
	height: '32px',
	borderRadius: token('radius.small'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: 'color.background.neutral.subtle',
});

const chevronHoverStyles = xcss({
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export enum BREADCRUMBS_MODES {
	ONLY_CONTENT_TITLE = 'ONLY_CONTENT_TITLE',
	ONLY_SPACE_NAME = 'ONLY_SPACE_NAME',
	FULL = 'FULL',
	HIDDEN = 'HIDDEN',
}

type BreadcrumbsWithSpaceNameDropdownProps = {
	contentTitle?: string;
	spaceKey: string;
	contentId?: string;
	testId?: string;
	showInlineRename?: boolean;
	shouldDisableMoveButton?: boolean;
	displayMode: BREADCRUMBS_MODES;
	contentLink?: string;
	contentType?: string;
	subType?: string;
};

export const BreadcrumbsWithSpaceNameDropdown = ({
	contentTitle,
	spaceKey,
	contentId,
	testId = 'breadcrumbs-with-space-name-dropdown',
	showInlineRename = false,
	shouldDisableMoveButton = false,
	displayMode,
	contentLink,
	contentType,
	subType,
}: BreadcrumbsWithSpaceNameDropdownProps): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isBreadcrumbsPopupOpen, setIsBreadcrumbsPopupOpen] = useState(false);
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { data: spaceData, error } = useSpaceDetail(spaceKey);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const spaceHomepageId = useSpaceHomepageId(spaceKey);
	const { push } = useRouteActions();
	const isModernEmbeddedConfluence = useEmbeddedModernHeader();
	const isLivePage = contentType === 'page' && subType === 'live';
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);

	const [{ isCollabOffline }] = useCollabNetworkStatus();

	// Network status is used when:
	// - isCollabOffline: The collab provider has detected a network disconnection
	// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
	// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
	const isNetworkDisabled =
		isCollabOffline &&
		expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
		!fg('platform_editor_offline_editing_wrapper_override');

	// Note don't check for loading as when refreshing data we will get data={...} and loading=true
	// In this case if we display the placeholder it will flash before the data is displayed
	// We should continue render with existing data and then update when the new data is available
	if (error || !spaceData) {
		return (
			<>
				{error && (
					<ErrorDisplay error={error}>
						<ExperienceFailure name={BREADCRUMBS_EXPERIENCE} error={error} />
					</ErrorDisplay>
				)}
				<BreadcrumbsPlaceholder />
			</>
		);
	}
	const spaceName = spaceData.name || spaceData.alias || spaceKey;
	const isSpaceNameLongerThan6Letters = spaceName?.length > 6;
	const baseContentTitle =
		contentTitle === '' ? intl.formatMessage(i18n.untitledPage) : contentTitle;
	const contentTitleToRender =
		isMarkdownMode && baseContentTitle ? `${baseContentTitle}.md` : baseContentTitle;
	const shouldOpenDropdown = contentTitleToRender || showInlineRename;
	const shouldShowCurrentContentTitle = contentTitleToRender && !showInlineRename;

	const spaceOverviewPath = buildContentPath({
		contentType: 'page',
		isSpaceOverview: true,
		query: spaceHomepageId
			? { [SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY]: spaceHomepageId }
			: undefined,
		spaceAlias: spaceData.alias,
		spaceKey,
	});

	const handleBreadcrumbDropdownTriggerClick = () => {
		if (shouldOpenDropdown) {
			if (!isBreadcrumbsPopupOpen) {
				experienceTracker.start({
					name: BREADCRUMBS_DROPDOWN_EXPERIENCE,
				});

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'opened',
						actionSubject: 'breadcrumbsDropdown',
						source: 'breadcrumbs',
						attributes: {
							isContentWrapper: true,
						},
					},
				}).fire();
			}

			setIsBreadcrumbsPopupOpen(!isBreadcrumbsPopupOpen);
		} else {
			push(spaceOverviewPath);
		}
	};

	const popupContent = () => {
		return (
			<BreadcrumbsDropdownContent
				spaceKey={spaceKey}
				contentId={contentId}
				closeDropdownOnClickOfBreadcrumbItem={closeDropdownOnClickOfBreadcrumbItem}
				spaceOverviewPath={spaceOverviewPath}
				shouldDisableMoveButton={shouldDisableMoveButton}
				contentTitle={contentTitleToRender}
			/>
		);
	};

	const closeDropdownOnClickOfBreadcrumbItem = () => {
		setIsBreadcrumbsPopupOpen(false);
	};

	const preloadBreadcrumbsDropdownOnSpaceNameHover = () => {
		void preloadBreadcrumbsDropdown({ spaceKey, contentId });
	};

	const renderPopup = () => (
		<>
			<Popup
				isOpen={isBreadcrumbsPopupOpen}
				onClose={() => setIsBreadcrumbsPopupOpen(false)}
				placement="bottom-start"
				content={popupContent}
				shouldRenderToParent
				trigger={(triggerProps) => (
					<button
						id="breadcrumbs-dropdown-chevron-button"
						data-testid="breadcrumbs-dropdown-chevron-button"
						css={[
							breadcrumbDropdownTriggerStyles,
							shouldOpenDropdown && maxWidthBreadcrumbsDropdownTriggerStyles,
							displayMode === BREADCRUMBS_MODES.ONLY_SPACE_NAME && onlySpaceNameTriggerStyles,
							displayMode === BREADCRUMBS_MODES.ONLY_CONTENT_TITLE &&
								maxWidthOnlyContentTitleTriggerStyles,
							displayMode === BREADCRUMBS_MODES.HIDDEN && hiddenStyles,
						]}
						disabled={isNetworkDisabled}
						onClick={handleBreadcrumbDropdownTriggerClick}
						onMouseEnter={preloadBreadcrumbsDropdownOnSpaceNameHover}
						onFocus={preloadBreadcrumbsDropdownOnSpaceNameHover}
						aria-label={intl.formatMessage(i18n.breadcrumbsChevronButtonAriaLabel)}
						{...triggerProps}
						{...ssrPlaceholderIdProp}
					>
						<Flex alignItems="center" gap="space.100">
							{shouldOpenDropdown && (
								<Tooltip
									content={
										!isNetworkDisabled && intl.formatMessage(i18n.breadcrumbsChevronButtonTooltip)
									}
									hideTooltipOnClick
								>
									{(tooltipProps) => (
										<Box
											xcss={[chevronBaseStyles, !isNetworkDisabled && chevronHoverStyles]}
											{...tooltipProps}
										>
											<ChevronDownIcon label="" color={token('color.icon.subtle')} size="small" />
										</Box>
									)}
								</Tooltip>
							)}

							{/* always render space name as dropdown trigger as we don't allow content title to be a dropdown trigger*/}
							{renderSpaceName()}
						</Flex>
					</button>
				)}
			/>
			{/* always render full breadcrumbs to allow dom manipulation to swap between different breadcrumbs display modes in useBreadcrumbsProgressiveHeader*/}
			{shouldOpenDropdown && renderSeparator()}
		</>
	);

	const renderContentTitle = () => {
		const titleElement = contentLink ? (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
			<a css={currentPageLinkBreadcrumbStyles} href={contentLink}>
				{contentTitleToRender}
			</a>
		) : (
			<div css={currentPageBreadcrumbStyles}>{contentTitleToRender}</div>
		);

		return (
			<span
				css={[
					contentTitleStyles,
					(displayMode === BREADCRUMBS_MODES.HIDDEN ||
						displayMode === BREADCRUMBS_MODES.ONLY_SPACE_NAME) &&
						hiddenContentTitleStyles,
					isModernEmbeddedConfluence && dropdownLessContentTitleStyles,
				]}
				id="breadcrumbs-content-title"
			>
				{(contentType === 'page' || contentType === 'blogpost') && (
					<ContentIcon
						type={contentType}
						subType={subType}
						color={token('color.text')}
						isMarkdownMode={isMarkdownMode}
					/>
				)}
				{titleElement}
			</span>
		);
	};

	const renderSpaceName = () => {
		return (
			<div
				id="breadcrumbs-space-name"
				css={[
					spaceNameTextStyles,
					shouldOpenDropdown && displayMode === BREADCRUMBS_MODES.FULL && noEllipsisStyles,
					shouldOpenDropdown &&
						isSpaceNameLongerThan6Letters &&
						displayMode === BREADCRUMBS_MODES.FULL &&
						fadedSpaceNameTextStyles, // always apply faded styles unless space name is very short. Need to use this approach instead of using ResizeObserver for SSR compatability.
					(displayMode === BREADCRUMBS_MODES.HIDDEN ||
						displayMode === BREADCRUMBS_MODES.ONLY_CONTENT_TITLE) &&
						hiddenStyles,
				]}
			>
				{spaceName}
			</div>
		);
	};

	const renderSeparator = () => {
		return (
			<div
				id="breadcrumbs-separator"
				css={[
					separatorStyles,
					!showInlineRename && hideSeparatorStyles,
					displayMode != BREADCRUMBS_MODES.FULL && hiddenStyles,
				]}
			>
				/
			</div>
		);
	};

	return (
		<Flex
			xcss={[breadcrumbsStyles, showInlineRename && inlineRenameBreadcrumbsStyles]}
			testId={testId}
			data-vc={testId}
		>
			{!isModernEmbeddedConfluence && renderPopup()}
			{shouldShowCurrentContentTitle && renderContentTitle()}
			<ExperienceSuccess name={BREADCRUMBS_EXPERIENCE} />
		</Flex>
	);
};

const i18n = defineMessages({
	breadcrumbsChevronButtonAriaLabel: {
		id: 'breadcrumbs.dropdown.button.chevron.aria-label',
		defaultMessage: 'Breadcrumbs',
		description: 'Aria-label for the breadcrumbs dropdown chevron button',
	},
	untitledPage: {
		id: 'breadcrumbs.dropdown.untitled-page.default-title',
		defaultMessage: 'Untitled',
		description: 'Title for an untitled draft page, draft blogpost, or live page for breadcrumbs',
	},
	breadcrumbsChevronButtonTooltip: {
		id: 'breadcrumbs.dropdown.button.chevron.tooltip',
		defaultMessage: 'Show navigation path',
		description:
			'Tooltip when hovering over the chevron button that will open the breadcrumbs dropdown onclick',
	},
});
